import { useDeferredValue, useEffect, useId, useRef, useState } from 'react';
import ActionSelect from './ActionSelect';
import { humanizeEntityType } from './auditFormat';
import { AuditActor, AuditFilter } from '../../types/audit';

export const MAX_Q_LENGTH = 100;
export const MAX_SPAN_DAYS = 366;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Inline validation of a Dubai date range (design §3: `to ≥ from`, span ≤ 366 days).
 * Returns the message to show, or null when the range may be sent.
 */
export function validateRange(from?: string, to?: string): string | null {
  if (from && to) {
    if (to < from) return '"To" must be on or after "From".';
    const days = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
    if (days > MAX_SPAN_DAYS) return 'Pick a range of one year or less.';
  }
  return null;
}

export function validateGarageId(garageId?: string): string | null {
  if (!garageId) return null;
  return UUID_RE.test(garageId.trim()) ? null : 'Enter a full garage id (UUID).';
}

/** All problems that block a request; empty when the filter can be sent. */
export function filterErrors(filter: AuditFilter): { range: string | null; garage: string | null } {
  return { range: validateRange(filter.from, filter.to), garage: validateGarageId(filter.garageId) };
}

interface AuditFiltersProps {
  value: AuditFilter;
  onChange: (next: AuditFilter) => void;
  actors: AuditActor[];
  actorsError?: string | null;
  entityTypes: string[];
}

/**
 * Spec 069 AC-B3 — the Audit toolbar. Every control writes the filter (mirrored in the URL by the
 * page); the text box is deferred so typing does not fire a request per key.
 */
export default function AuditFilters({ value, onChange, actors, actorsError, entityTypes }: AuditFiltersProps) {
  const uid = useId();
  const [q, setQ] = useState(value.q ?? '');
  const [garage, setGarage] = useState(value.garageId ?? '');
  const deferredQ = useDeferredValue(q);
  // The latest filter and callback, so the delayed text push never reverts a change made meanwhile.
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };

  // Keep the drafts in step when the URL changes underneath (Back, "Last 30 days", reset).
  useEffect(() => { setQ(value.q ?? ''); }, [value.q]);
  useEffect(() => { setGarage(value.garageId ?? ''); }, [value.garageId]);

  useEffect(() => {
    const next = deferredQ.trim().slice(0, MAX_Q_LENGTH);
    if (next === (latest.current.value.q ?? '')) return;
    const t = window.setTimeout(() => {
      const { value: v, onChange: push } = latest.current;
      if (next !== (v.q ?? '')) push({ ...v, q: next || undefined });
    }, 300);
    return () => window.clearTimeout(t);
  }, [deferredQ]);

  const rangeError = validateRange(value.from, value.to);
  const garageError = validateGarageId(garage.trim() || undefined);
  const set = (patch: Partial<AuditFilter>) => onChange({ ...value, ...patch });

  const commitGarage = () => {
    const g = garage.trim();
    if (g === (value.garageId ?? '')) return;
    if (!g || !validateGarageId(g)) set({ garageId: g || undefined });
  };

  return (
    <div className="audit-filters" role="search" aria-label="Filter admin actions">
      <div className="audit-field">
        <label htmlFor={`${uid}-actor`} className="audit-field__label">Admin</label>
        <select
          id={`${uid}-actor`}
          className="admin-select"
          value={value.adminSubjectId ?? ''}
          onChange={(e) => set({ adminSubjectId: e.target.value || undefined })}
        >
          <option value="">{actorsError ? 'All admins (list unavailable)' : 'All admins'}</option>
          {actors.map((a) => (
            <option key={a.subjectId} value={a.subjectId}>{a.name || `Admin · ${a.subjectId.slice(-8)}`}</option>
          ))}
          {value.adminSubjectId && !actors.some((a) => a.subjectId === value.adminSubjectId) && (
            <option value={value.adminSubjectId}>Admin · {value.adminSubjectId.slice(-8)}</option>
          )}
        </select>
      </div>

      <ActionSelect multiple label="Action" value={value.actions ?? []} onChange={(actions) => set({ actions: actions.length ? actions : undefined })} />

      <div className="audit-field">
        <label htmlFor={`${uid}-entity`} className="audit-field__label">Entity</label>
        <select
          id={`${uid}-entity`}
          className="admin-select"
          value={value.entityType ?? ''}
          onChange={(e) => set({ entityType: e.target.value || undefined })}
        >
          <option value="">All entities</option>
          {entityTypes.map((t) => <option key={t} value={t}>{humanizeEntityType(t)}</option>)}
        </select>
      </div>

      <div className="audit-field">
        <label htmlFor={`${uid}-garage`} className="audit-field__label">Garage id</label>
        <input
          id={`${uid}-garage`}
          type="text"
          className="admin-search-input audit-input--id"
          placeholder="Garage UUID"
          value={garage}
          onChange={(e) => setGarage(e.target.value)}
          onBlur={commitGarage}
          onKeyDown={(e) => { if (e.key === 'Enter') commitGarage(); }}
          aria-invalid={!!garageError}
          aria-describedby={garageError ? `${uid}-garage-err` : undefined}
        />
        {garageError && <span id={`${uid}-garage-err`} className="audit-hint audit-hint--error" role="alert">{garageError}</span>}
      </div>

      <div className="audit-field">
        <label htmlFor={`${uid}-from`} className="audit-field__label">From (GST)</label>
        <input
          id={`${uid}-from`}
          type="date"
          className="admin-form-input"
          value={value.from ?? ''}
          onChange={(e) => set({ from: e.target.value || undefined })}
          aria-invalid={!!rangeError}
          aria-describedby={rangeError ? `${uid}-range-err` : undefined}
        />
      </div>
      <div className="audit-field">
        <label htmlFor={`${uid}-to`} className="audit-field__label">To (GST)</label>
        <input
          id={`${uid}-to`}
          type="date"
          className="admin-form-input"
          value={value.to ?? ''}
          onChange={(e) => set({ to: e.target.value || undefined })}
          aria-invalid={!!rangeError}
          aria-describedby={rangeError ? `${uid}-range-err` : undefined}
        />
      </div>

      <div className="audit-field audit-field--grow">
        <label htmlFor={`${uid}-q`} className="audit-field__label">Search</label>
        <input
          id={`${uid}-q`}
          type="search"
          className="admin-search-input"
          placeholder="Entity name or id"
          maxLength={MAX_Q_LENGTH}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {rangeError && (
        <p id={`${uid}-range-err`} className="audit-hint audit-hint--error audit-filters__error" role="alert">{rangeError}</p>
      )}
    </div>
  );
}
